import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  InlineRow,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
} from '../../components/ui';
import { useMinimumWageForQuery } from '../../services/serviceService/__generated__/queries.generated';
import { OfferingsEditor } from './OfferingsEditor';
import { belowWageCount, formatMoney, SERVICE_TYPES } from './priceTables';
import { serviceErrorMessage } from './serviceErrors';
import { workerManagePath } from './servicePaths';
import { useOfferingsEditor, useWorkerEditor } from './useWorkerEditor';
import { AreaSection } from './WorkerSections';
import { SECTION_FIELDS } from './workerForm';

/**
 * Every price of one worker on one page (spec 019 W7a, D26): home turnover,
 * the vehicle table, the handover rate and the add-ons. Each price shows what
 * it works out to per hour; prices below the local minimum wage get a
 * warning and a count at the top. Saving is never blocked by a warning.
 * Changes apply to new jobs only.
 */
export default function WorkerPricingPage() {
  const { id = '' } = useParams<{ id: string }>();
  const editor = useWorkerEditor(id);
  const worker = editor.worker;
  const offerings = useOfferingsEditor(worker);
  const locationId = worker?.car.location?.id;
  const wageQuery = useMinimumWageForQuery({
    variables: { locationId: Number(locationId ?? 0) },
    skip: !locationId,
    fetchPolicy: 'cache-first',
  });
  const wage = wageQuery.data?.minimumWageFor ?? null;
  const [areaMessage, setAreaMessage] = useState<string | null>(null);

  if (editor.loading) {
    return (
      <PageContainer width="lg" canvas>
        <LoadingBlock label="Loading prices" />
      </PageContainer>
    );
  }
  if (!worker) {
    return (
      <PageContainer width="lg" canvas>
        <Alert tone="error" title="Worker not found">
          {serviceErrorMessage(editor.loadError, 'This worker could not be loaded.')}
        </Alert>
      </PageContainer>
    );
  }

  const forms = SERVICE_TYPES.map(type => offerings.forms[type]);
  const lowCount = belowWageCount(forms, wage?.hourlyRate);

  const onSaveArea = async () => {
    setAreaMessage(null);
    const result = await editor.save(SECTION_FIELDS.area);
    if (result.ok) toast.success('Service area saved');
    else if (result.message) setAreaMessage(result.message);
  };

  const onSave = async () => {
    const result = await offerings.saveAll();
    if (result.ok) toast.success('Prices saved. New jobs use them.');
  };

  return (
    <PageContainer width="lg" canvas data-testid="worker-pricing">
      <PageHeader
        eyebrow="Prices"
        title={`${worker.worker.displayName}'s prices`}
        description="Changes apply to new jobs only. Booked jobs keep their price."
        backTo={{ to: workerManagePath(id), label: worker.worker.displayName }}
        actions={
          <InlineRow>
            <Button type="button" theme="secondary" text="Undo changes" onClick={offerings.reset} />
            <Button
              type="button"
              text="Save prices"
              onClick={onSave}
              isLoading={offerings.saving}
              dataTestId="pricing-save"
            />
          </InlineRow>
        }
      />
      <Stack $gap={6}>
        {wage && (
          <Alert
            tone={lowCount > 0 ? 'warning' : 'neutral'}
            title={
              lowCount > 0
                ? `${lowCount === 1 ? '1 price works' : `${lowCount} prices work`} out below the local minimum wage`
                : 'All prices are at or above the local minimum wage'
            }
            data-testid="wage-summary"
          >
            The minimum wage here is {formatMoney(wage.hourlyRate)} an hour ({wage.source}). We
            estimate the time each job takes and show the price per hour next to each price. You set
            your own prices: this is a reminder only.
          </Alert>
        )}
        {offerings.message && (
          <Alert tone="error" title="Prices not saved">
            {offerings.message}
          </Alert>
        )}
        <OfferingsEditor
          forms={offerings.forms}
          errors={offerings.errors}
          onChange={offerings.change}
          minimumWage={wage?.hourlyRate ?? null}
          serverWarnings={worker.priceWarnings}
          handoverAllowed={worker.worker.backgroundCheckVerified}
        />
        <InlineRow>
          <Button type="button" text="Save prices" onClick={onSave} isLoading={offerings.saving} />
        </InlineRow>
        <Card data-testid="pricing-travel">
          <CardHeader
            title="Travel past the service area"
            description="The travel price past the service area is part of the service area settings."
          />
          <CardBody>
            <Stack $gap={4}>
              {areaMessage && (
                <Alert tone="error" title="Not saved">
                  {areaMessage}
                </Alert>
              )}
              <AreaSection form={editor.form} errors={editor.errors} onChange={editor.change} />
              <InlineRow>
                <Button
                  type="button"
                  theme="secondary"
                  text="Save area and travel"
                  onClick={onSaveArea}
                  isLoading={editor.saving}
                  dataTestId="pricing-save-area"
                />
              </InlineRow>
            </Stack>
          </CardBody>
        </Card>
      </Stack>
    </PageContainer>
  );
}
